import { jsxs, jsx } from "react/jsx-runtime";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { useTranslation } from "react-i18next";
import "../shared/quiz-style-context.js";
import "react";
import { SubmitButton } from "../shared/submit-button.js";
import { QuizImage } from "../shared/quiz-image.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { QUIZ_OPTION_STATES } from "../shared/quiz-styles.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useMultiChoiceQuiz } from "../hooks/use-multi-choice-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import Check from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/check.js";
import X from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/x.js";
function MultiChoiceQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false
}) {
  const { t } = useTranslation("quiz");
  const {
    selectedKeys,
    sortedAnswers,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySet,
    correctsBySelectedKey,
    gridContainerId,
    handleSelect,
    handleSubmit
  } = useMultiChoiceQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    quiz.subtype === "C" && /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: {
      __html: `
            @media (min-width: 768px) {
              #${gridContainerId} {
                grid-template-columns: repeat(${sortedAnswers.length}, minmax(0, 1fr));
              }
            }
          `
    } }),
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.multiChoice.label"),
        title: quiz.title,
        difficultyLevel,
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        problem: quiz.problem && /* @__PURE__ */ jsx("div", { className: "text-xl leading-relaxed text-gray-900", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) })
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        id: quiz.subtype === "C" ? gridContainerId : void 0,
        role: "listbox",
        "aria-label": t("types.multiChoice.selectOption"),
        "aria-multiselectable": "true",
        className: cn(
          quiz.subtype === "B" && "grid grid-cols-1 md:grid-cols-2 gap-3",
          quiz.subtype === "C" && "grid grid-cols-1 gap-3",
          (!quiz.subtype || quiz.subtype !== "B" && quiz.subtype !== "C") && "space-y-3"
        ),
        children: sortedAnswers.map((answer, idx) => {
          const isSelected = selectedKeys.includes(answer.key);
          const isInResolvedCorrectAnswers = resolvedCorrectKeySet.has(answer.key);
          const isCorrectByCorrects = correctsBySelectedKey.get(answer.key);
          const showAsCorrect = isSolved && (shouldShowCorrectAnswers ? isInResolvedCorrectAnswers : isSelected && (isCorrectByCorrects !== void 0 ? isCorrectByCorrects : isInResolvedCorrectAnswers));
          const isWrongSelected = isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect;
          return /* @__PURE__ */ jsxs(
            motion.button,
            {
              role: "option",
              "aria-selected": isSelected,
              initial: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0 },
              transition: { delay: idx * 0.05 },
              onClick: () => handleSelect(answer.key),
              disabled: isSolved || isViewSolved,
              className: cn(
                "w-full p-4 rounded-xl border-2 text-left transition-all",
                "flex items-center gap-4 text-xl",
                // 기본 상태
                !isSolved && !isSelected && QUIZ_OPTION_STATES.default,
                !isSolved && !isSelected && "hover:border-blue-400 hover:bg-blue-50",
                // 선택 상태
                !isSolved && isSelected && QUIZ_OPTION_STATES.selected,
                // 정답
                showAsCorrect && QUIZ_OPTION_STATES.correct,
                // 오답
                isWrongSelected && QUIZ_OPTION_STATES.wrong,
                // 비활성화
                isSolved && !showAsCorrect && !isWrongSelected && QUIZ_OPTION_STATES.disabled,
                (isSolved || isViewSolved) && "cursor-not-allowed"
              ),
              children: [
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all",
                      // 기본 (풀이 전, 미선택)
                      !isSelected && !isSolved && "border-gray-400 bg-white",
                      // 선택 (풀이 전)
                      isSelected && !isSolved && "border-blue-500 bg-blue-500 shadow-[0_0_0_3px_#eff6ff]",
                      // 정답 스타일
                      showAsCorrect && "border-green-500 bg-green-500",
                      // 오답 스타일
                      isWrongSelected && "border-red-500 bg-red-500",
                      // 비활성화 (풀이 후, 정답도 오답도 아닌 항목)
                      isSolved && !showAsCorrect && !isWrongSelected && "border-gray-300 bg-gray-100"
                    ),
                    children: [
                      !isSelected && !isSolved && /* @__PURE__ */ jsx("span", { className: "text-[10px] font-bold text-gray-400 leading-none", children: idx + 1 }),
                      isSelected && !isSolved && /* @__PURE__ */ jsx(Check, { className: "w-3 h-3 text-white" }),
                      showAsCorrect && /* @__PURE__ */ jsx(Check, { className: "w-3 h-3 text-white" }),
                      isWrongSelected && /* @__PURE__ */ jsx(X, { className: "w-3 h-3 text-white" }),
                      isSolved && !showAsCorrect && !isWrongSelected && /* @__PURE__ */ jsx("span", { className: "text-[10px] font-bold text-gray-400 leading-none", children: idx + 1 })
                    ]
                  }
                ),
                answer.image && /* @__PURE__ */ jsx(
                  QuizImage,
                  {
                    image: answer.image,
                    alt: t("types.multiChoice.optionAlt", { number: idx + 1 }),
                    className: "my-0 shrink-0"
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: "flex-1 font-medium text-gray-700", children: /* @__PURE__ */ jsx(KatexText, { text: answer.answer }) })
              ]
            },
            answer.key
          );
        })
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? Array.from(resolvedCorrectKeySet).map((key) => {
          var _a;
          return (_a = sortedAnswers.find((a) => a.key === key)) == null ? void 0 : _a.answer;
        }).filter(Boolean).join(", ") : void 0,
        disableSound,
        isViewSolved
      }
    ),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !(solveResult == null ? void 0 : solveResult.correct),
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  MultiChoiceQuizAdult
};
//# sourceMappingURL=multi-choice-adult.js.map
